import React from "react";
import { useField } from "formik";
import { Checkbox as MuiCheckbox, Switch, FormControlLabel, FormControlLabelProps, FormGroup, FormHelperText, FormControl, CheckboxProps, RadioGroup, Radio } from "@material-ui/core";

export type CheckboxWithErrorLabelProps =
  { name: string; label: React.ReactNode } & Omit<FormControlLabelProps, "control" | "label">;

type CheckboxWithLabelProps = CheckboxWithErrorLabelProps & {
  margin?: "none" | "dense" | "normal"
  size?: "small" | "medium"
  defaultChecked?:boolean;
  checked?: boolean;
  onChange?: CheckboxProps["onChange"];
}

export const CheckboxWithLabel: React.FC<CheckboxWithLabelProps> =
  ({ name, label, size, checked, margin = "none", defaultChecked, onChange, ...inputProps }) =>
    <FormControl margin={margin}>
      <FormControlLabel
        {...inputProps}
        control={<MuiCheckbox
          id={`hc-chk-${name}`}
          name={name}
          style={size === "small" ? { padding: "5px" } : {}}
          size={size} checked={checked} onChange={onChange} defaultChecked={defaultChecked}/>}
        label={label} />
    </FormControl>;

export const CheckboxWithErrorLabel: React.FC<CheckboxWithErrorLabelProps> = ({
  name, label, ...inputProps
}) => {
  const [field, meta] = useField(name);
  return (
    <FormControl>
      <FormControlLabel
        {...field}
        {...inputProps}
        control={<MuiCheckbox id={`hc-chk-${name}`} checked={meta.value} />}
        label={label} />
      {meta.touched && meta.error && (
        <FormHelperText error>
          {meta.error}
        </FormHelperText>
      )}
    </FormControl>);
};

const TERMS_URL = "https://s3.amazonaws.com/hc-static/pdfs/applicant-terms.pdf";
const PRIV_URL = "https://s3.amazonaws.com/hc-static/pdfs/privacy-policy.pdf";

const TosLabel: React.FC = () => (
  <>
    I agree to the{" "}
    <a href={TERMS_URL} target="_blank" rel="noreferrer">
      Terms of Use
    </a>{" "}
    and{" "}
    <a href={PRIV_URL} target="_blank" rel="noreferrer">
      Privacy Policy
    </a>
  </>
);

type TosCheckboxProps = { name: string }
export const TosCheckbox:React.FC<TosCheckboxProps> = ({ name }) => (
  <CheckboxWithErrorLabel label={<TosLabel />} name={name} />
);

export const SwitchWithErrorLabel: React.FC<CheckboxWithErrorLabelProps> = ({
  name, label, ...inputProps
}) => {
  const [field, meta] = useField(name);
  return (
    <FormGroup>
      <FormControlLabel
        {...field}
        {...inputProps}
        control={<Switch checked={field.value} id={`hc-chk-${name}`} />}
        label={label} />
      {meta.touched && meta.error && (
        <FormHelperText error>
          {meta.error}
        </FormHelperText>
      )}
    </FormGroup>
  );
};

interface YesNoRadioProps {
  name: string;
  label: string;
}

export const YesNoRadio: React.FC<YesNoRadioProps> = ({
  name,
  label,
}) => {
  const [field, meta, helpers] = useField(name);
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    helpers.setValue(event.target.value === "yes");
  };

  return (
    <FormControl component="fieldset" error={meta.touched && !!meta.error}>
      <label>{label}</label>
      <RadioGroup
        row
        {...field}
        aria-label={name}
        name={name}
        value={field.value ? "yes" : "no"}
        onChange={handleChange}
      >
        <FormControlLabel value="yes" control={<Radio />} label="Yes" />
        <FormControlLabel value="no" control={<Radio />} label="No" />
      </RadioGroup>
      {meta.touched && meta.error && (
        <FormHelperText>{meta.error}</FormHelperText>
      )}
    </FormControl>
  );
};


export const Checkbox = CheckboxWithErrorLabel;
